import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import { Home, UserPlus, Trash2, Shield, ShieldCheck, Crown, Users, ClipboardList, Settings2, ArrowRight, GitBranch, Zap, Bell, Palette, Shapes, Archive, Workflow as WorkflowIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useDeleteProject,
  useRemoveMember,
  useUpdateMemberRole,
  useUpdateProject,
  projectKeys,
} from '../hooks/useProjects';
import { useAuthStore } from '../store/authStore';
import { useProjectStore } from '../store/projectStore';
import type { ProjectDetail, ProjectMember } from '../services/projectService';
import TemplateManagement from '../components/settings/TemplateManagement';
import MemberInviteModal from '../components/settings/MemberInviteModal';
import ArchiveProjectModal from '../components/settings/ArchiveProjectModal';
import AccessRequestHistory from '../components/settings/AccessRequestHistory';
import WorkflowMigrationModal from '../components/workflow/WorkflowMigrationModal';
import IssueTypeWorkflowMappingSection from '../components/workflow/IssueTypeWorkflowMappingSection';
import GitHubSettingsTab from '../components/github/GitHubSettingsTab';
import AutomationRulesTab from '../components/automation/AutomationRulesTab';
import NotificationSettingsTab from '../components/settings/NotificationSettingsTab';
import FigmaSettingsTab from '../components/figma/FigmaSettingsTab';
import DesignSettingsTab from '../components/design/DesignSettingsTab';
import ProjectIssueTypeSettingsTab from '../components/settings/ProjectIssueTypeSettingsTab';
import { useProjectIssueTypes } from '../hooks/useProjectIssueTypes';
import { TOGGLEABLE_MENUS, PROJECT_MENU_KEYS } from '../constants/projectMenus';
import { figmaKeys } from '../hooks/useFigma';
import { getContrastText } from '../components/issue/IssueListView';
import Spinner from '../components/ui/Spinner';
import Avatar from '../components/common/Avatar';
import { useProfileModal } from '../components/common/ProfileModalProvider';
import type { ProfileColorKey } from '@maios/shared';

const ROLE_LABEL: Record<string, string> = {
  PROJECT_MANAGER: '프로젝트 매니저',
  PROJECT_LEADER: '프로젝트 리더',
  MEMBER: '멤버',
  STAKEHOLDER: '이해관계자',
};

const ROLE_OPTIONS = ['PROJECT_LEADER', 'MEMBER', 'STAKEHOLDER'] as const;

type SettingsTab = 'home' | 'general' | 'members' | 'templates' | 'issue-types' | 'workflow' | 'github' | 'design' | 'automation' | 'notifications' | 'access-history';

const SETTINGS_TABS: { key: SettingsTab; label: string; icon: React.ReactNode }[] = [
  // CHG-158 REQ-PS-001: 홈 탭 (이름/키/공개설정)
  { key: 'home', label: '홈', icon: <Home size={16} /> },
  // CHG-158 REQ-PS-003: "일반" → "메뉴" (내용은 메뉴 관리 그대로)
  { key: 'general', label: '메뉴', icon: <Settings2 size={16} /> },
  { key: 'members', label: '멤버', icon: <Users size={16} /> },
  { key: 'templates', label: '템플릿', icon: <ClipboardList size={16} /> },
  { key: 'issue-types', label: '이슈 타입', icon: <Shapes size={16} /> },
  { key: 'workflow', label: '워크플로우', icon: <WorkflowIcon size={16} /> },
  { key: 'github', label: 'GitHub', icon: <GitBranch size={16} /> },
  { key: 'design', label: '디자인', icon: <Palette size={16} /> },
  { key: 'automation', label: '자동화', icon: <Zap size={16} /> },
  { key: 'notifications', label: '알림', icon: <Bell size={16} /> },
  // CHG-132 Phase E REQ-PA-023: 보관 접근 기록
  // CHG-158 REQ-PS-004: "보관 접근 기록" → "보안" (위험 구역 이동으로 내용 확장)
  { key: 'access-history', label: '보안', icon: <ShieldCheck size={16} /> },
];

export default function ProjectSettingsPage() {
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { openProfileModal } = useProfileModal();
  const queryClient = useQueryClient();
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);

  const { data: projectIssueTypesData } = useProjectIssueTypes(project.id);
  const deleteProjectMutation = useDeleteProject();
  const removeMemberMutation = useRemoveMember();
  const updateRoleMutation = useUpdateMemberRole();
  const updateProjectMutation = useUpdateProject();

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    // figma OAuth 콜백 시 자동으로 figma 탭 활성화
    if (searchParams.has('figma')) return 'design';
    return 'general';
  });
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  // CHG-158 REQ-PS-002: 홈 탭 — 이름/공개설정 draft.
  // activeTab === 'home' 조건부 블록 안에서 훅을 호출하면 탭 전환 시 Rules of Hooks를 위반하므로
  // 컴포넌트 최상단에서 항상 선언하고, JSX 렌더링만 activeTab으로 분기한다.
  const [nameDraft, setNameDraft] = useState(project.name);
  const [visibilityDraft, setVisibilityDraft] = useState(project.visibility);
  useEffect(() => {
    setNameDraft(project.name);
    setVisibilityDraft(project.visibility);
  }, [project.name, project.visibility]);
  const isHomeDirty = nameDraft !== project.name || visibilityDraft !== project.visibility;

  // Current user's project role
  const myMembership = project.members.find((m) => m.user.id === user?.id);
  const isProjectManager = myMembership?.projectRole === 'PROJECT_MANAGER' || user?.role === 'SUPER_ADMIN';
  const isProjectAdmin = isProjectManager || myMembership?.projectRole === 'PROJECT_LEADER';

  // Figma OAuth 콜백 처리 (CHG-049)
  useEffect(() => {
    const figmaParam = searchParams.get('figma');
    if (!figmaParam) return;

    if (figmaParam === 'connected') {
      toast.success('Figma가 연결되었습니다.');
      queryClient.invalidateQueries({ queryKey: figmaKeys.connection(project.id) });
    } else if (figmaParam === 'error') {
      const message = searchParams.get('message') || 'Figma 연결에 실패했습니다.';
      toast.error(decodeURIComponent(message));
    }

    // URL에서 figma 파라미터 제거 (히스토리 깔끔하게)
    searchParams.delete('figma');
    searchParams.delete('message');
    setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // CHG-043 REQ-V43-005: 마지막 PM 보호
  const pmCount = project.members.filter((m) => m.projectRole === 'PROJECT_MANAGER').length;

  const handleRoleChange = async (member: ProjectMember, newRole: string) => {
    try {
      await updateRoleMutation.mutateAsync({
        projectId: project.id,
        memberId: member.id,
        projectRole: newRole,
      });
      toast.success(`${member.user.name}의 역할이 변경되었습니다.`);
    } catch {
      toast.error('역할 변경에 실패했습니다.');
    }
  };

  const handleRemoveMember = async (member: ProjectMember) => {
    if (!confirm(`${member.user.name}을(를) 프로젝트에서 제거하시겠습니까?`)) return;

    try {
      await removeMemberMutation.mutateAsync({
        projectId: project.id,
        memberId: member.id,
      });
      toast.success(`${member.user.name}이(가) 프로젝트에서 제거되었습니다.`);
    } catch {
      toast.error('멤버 제거에 실패했습니다.');
    }
  };

  const handleUpdateProject = async (data: Record<string, unknown>) => {
    try {
      await updateProjectMutation.mutateAsync({ id: project.id, data });
      toast.success('프로젝트 설정이 변경되었습니다.');
    } catch {
      toast.error('설정 변경에 실패했습니다.');
    }
  };

  // CHG-158: 홈 탭 저장 — name/visibility만 전송(projectKey는 읽기 전용이라 payload에 절대 포함하지 않음)
  const handleSaveHome = async () => {
    if (!isProjectManager) return;
    await handleUpdateProject({ name: nameDraft, visibility: visibilityDraft });
  };

  const handleDeleteProject = async () => {
    if (deleteConfirmText !== project.projectKey) return;

    try {
      await deleteProjectMutation.mutateAsync(project.id);
      setCurrentProject(null);
      toast.success('프로젝트가 삭제되었습니다.');
      navigate('/projects');
    } catch {
      toast.error('프로젝트 삭제에 실패했습니다.');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* REQ-V19-011: 설정 탭 — 고정 영역 */}
      <div className="flex items-center gap-3 shrink-0 pb-4">
        <h1 className="text-xl font-bold text-gray-900">설정</h1>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          {SETTINGS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 스크롤 영역 */}
      <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {/* 홈 탭 — 이름/키(읽기 전용)/공개설정 (CHG-158 REQ-PS-002) */}
      {activeTab === 'home' && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">
          {/* 프로젝트 이름 */}
          <div>
            <label className="text-sm font-semibold text-gray-900">프로젝트 이름</label>
            <input
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              disabled={!isProjectManager}
              maxLength={50}
              className="mt-1.5 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
            />
          </div>

          {/* 프로젝트 키 — 항상 읽기 전용 (CHG-158 핵심 정책: 키 불변) */}
          <div>
            <label className="text-sm font-semibold text-gray-900">프로젝트 키</label>
            <input
              value={project.projectKey}
              disabled
              className="mt-1.5 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
            />
            <p className="text-sm text-gray-400 mt-1">
              프로젝트 키는 이슈·요구사항·테스트케이스 번호에 이미 사용되고 있어 생성 후에는 변경할 수 없습니다.
            </p>
          </div>

          {/* 공개설정 — ProjectCreatePage.tsx와 동일한 라벨/설명/카드 스타일 재사용 */}
          <div>
            <label className="text-sm font-semibold text-gray-900">공개설정</label>
            <div className="mt-1.5 flex gap-3">
              {[
                { value: 'PRIVATE', label: 'Private', desc: '멤버만 접근 가능' },
                { value: 'PUBLIC', label: 'Public', desc: '조직 전체 공개' },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`relative flex flex-col items-center px-4 py-3 border rounded-lg text-center transition-all ${
                    visibilityDraft === opt.value
                      ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${isProjectManager ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                >
                  <input
                    type="radio"
                    name="visibility"
                    value={opt.value}
                    checked={visibilityDraft === opt.value}
                    onChange={() => setVisibilityDraft(opt.value)}
                    disabled={!isProjectManager}
                    className="sr-only"
                  />
                  <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                  <span className="text-sm text-gray-500 mt-0.5">{opt.desc}</span>
                </label>
              ))}
            </div>
            {/* 레거시 INTERNAL 값 방어: 옵션 목록엔 없지만 현재 값이 INTERNAL이어도 깨지지 않게 안내만 표시 */}
            {project.visibility === 'INTERNAL' && (
              <p className="text-sm text-amber-600 mt-2">
                현재 값: INTERNAL (아직 선택 가능한 옵션이 아닙니다 — PUBLIC/PRIVATE 중 하나를 선택해 저장하면 되돌릴 수 없습니다.)
              </p>
            )}
          </div>

          {isProjectManager && (
            <div className="pt-2 border-t border-gray-100 flex justify-end">
              <button
                onClick={handleSaveHome}
                disabled={!isHomeDirty || updateProjectMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                저장
              </button>
            </div>
          )}
        </div>
      )}

      {/* 일반 설정 탭 (CHG-031 REQ-V31-017, REQ-V31-018, CHG-055 REQ-V55-003) */}
      {activeTab === 'general' && (() => {
        const currentMenus: string[] = project.enabledMenus ?? PROJECT_MENU_KEYS[project.projectType] ?? PROJECT_MENU_KEYS.SCRUM;
        // 기본 메뉴 옵션: enabledMenus에 포함된 메뉴 중 settings 제외
        const defaultMenuOptions = TOGGLEABLE_MENUS.filter(
          (m) => m.key !== 'settings' && currentMenus.includes(m.key),
        );

        const handleMenuToggle = async (menuKey: string, enabled: boolean) => {
          const newMenus = enabled
            ? [...currentMenus, menuKey]
            : currentMenus.filter((k) => k !== menuKey);
          const payload: Record<string, unknown> = { enabledMenus: newMenus };
          // 비활성화하는 메뉴가 현재 defaultMenu이면 dashboard로 변경
          if (!enabled && project.defaultMenu === menuKey) {
            payload.defaultMenu = 'dashboard';
          }
          await handleUpdateProject(payload);
        };

        const handleResetToPreset = async () => {
          const preset = PROJECT_MENU_KEYS[project.projectType] ?? PROJECT_MENU_KEYS.SCRUM;
          if (!confirm(`현재 프로젝트 유형(${project.projectType})의 기본 프리셋으로 메뉴를 초기화하시겠습니까?`)) return;
          await handleUpdateProject({ enabledMenus: preset });
        };

        return (
        <div className="space-y-6">
          {/* 기본 메뉴 선택 [REQ-V55-016] */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">기본 메뉴</h3>
                <p className="text-sm text-gray-500 mt-0.5">프로젝트 진입 시 처음 표시되는 메뉴를 선택합니다.</p>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                {defaultMenuOptions.map((option) => (
                  <label key={option.key} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="defaultMenu"
                      value={option.key}
                      checked={project.defaultMenu === option.key}
                      onChange={() => handleUpdateProject({ defaultMenu: option.key })}
                      disabled={!isProjectAdmin}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* 메뉴 관리 [REQ-V55-003] */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-900">메뉴 관리</h3>
              <p className="text-sm text-gray-500 mt-0.5">프로젝트에서 사용할 메뉴를 선택합니다.</p>
            </div>
            <div className="divide-y divide-gray-100">
              {TOGGLEABLE_MENUS.map((menu) => {
                const isOn = currentMenus.includes(menu.key);
                const isFixed = !!menu.fixed;
                const canToggle = isProjectAdmin && !isFixed;

                return (
                  <div key={menu.key} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <menu.icon className="size-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{menu.label}</div>
                        <div className="text-sm text-gray-500">{menu.description}</div>
                        {(menu.key === 'code-review' || menu.key === 'metrics') && (
                          <div className="text-sm text-orange-500 mt-0.5">* GitHub Repository 연동 후 표시됩니다.</div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => canToggle && handleMenuToggle(menu.key, !isOn)}
                      disabled={!canToggle}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        isOn ? 'bg-primary-600' : 'bg-gray-200'
                      } ${!canToggle ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isOn ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
            {isProjectAdmin && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={handleResetToPreset}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  프리셋으로 초기화
                </button>
              </div>
            )}
          </div>

        </div>
        );
      })()}

      {/* 멤버 탭 */}
      {activeTab === 'members' && (
        <>
          {/* Members Section */}
          <div className="bg-white border border-gray-200 rounded-xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">프로젝트 멤버</h3>
                <p className="text-sm text-gray-500 mt-0.5">{project.members.length}명 참여 중</p>
              </div>
              {isProjectManager && (
                <button
                  onClick={() => setIsInviteModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium
                    hover:bg-primary-700 transition-colors"
                >
                  <UserPlus className="size-4" />
                  멤버 초대
                </button>
              )}
            </div>

            {/* Members table */}
            <div className="divide-y divide-gray-100">
              {project.members.map((member) => (
                <div key={member.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors">
                  {/* Avatar */}
                  <Avatar
                    size="sm"
                    userId={member.user.id}
                    name={member.user.name}
                    avatarUrl={member.user.avatarUrl}
                    displayColor={member.user.displayColor as ProfileColorKey | null | undefined}
                    firstName={member.user.firstName}
                    onClick={() => openProfileModal(member.user.id)}
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{member.user.name}</span>
                      {member.projectRole === 'PROJECT_MANAGER' && (
                        <Crown className="size-3.5 text-amber-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">{member.user.email}</p>
                  </div>

                  {/* Role badge / selector */}
                  <div className="flex items-center gap-2">
                    {(() => {
                      const isLastPm = member.projectRole === 'PROJECT_MANAGER' && pmCount <= 1;

                      // PM만 역할 변경 가능
                      if (!isProjectManager) {
                        return (
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium ${
                            member.projectRole === 'PROJECT_MANAGER' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {member.projectRole === 'PROJECT_MANAGER' && <Shield className="size-3" />}
                            {ROLE_LABEL[member.projectRole]}
                          </span>
                        );
                      }

                      // PM이 마지막 PM이면 드롭다운 비활성화 + 툴팁
                      if (isLastPm) {
                        return (
                          <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium bg-amber-50 text-amber-700"
                            title="최소 1명의 프로젝트 매니저가 필요합니다"
                          >
                            <Shield className="size-3" />
                            {ROLE_LABEL[member.projectRole]}
                          </span>
                        );
                      }

                      // PM인 멤버도 역할 변경 가능 (PM이 2명 이상일 때)
                      return (
                        <select
                          value={member.projectRole}
                          onChange={(e) => handleRoleChange(member, e.target.value)}
                          className="px-2 py-1 border border-gray-200 rounded text-sm font-medium text-gray-700 outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          <option value="PROJECT_MANAGER">{ROLE_LABEL.PROJECT_MANAGER}</option>
                          {ROLE_OPTIONS.map((role) => (
                            <option key={role} value={role}>
                              {ROLE_LABEL[role]}
                            </option>
                          ))}
                        </select>
                      );
                    })()}

                    {/* Remove button: PM만, 자기 자신 제거 불가, 마지막 PM 제거 불가 */}
                    {isProjectManager && member.userId !== user?.id && !(member.projectRole === 'PROJECT_MANAGER' && pmCount <= 1) && (
                      <button
                        onClick={() => handleRemoveMember(member)}
                        className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                        title="멤버 제거"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 멤버 초대 모달 */}
          <MemberInviteModal
            isOpen={isInviteModalOpen}
            onClose={() => setIsInviteModalOpen(false)}
            projectId={project.id}
            onInvited={() => {
              queryClient.invalidateQueries({ queryKey: projectKeys.detail(project.id) });
            }}
          />
        </>
      )}

      {/* 템플릿 탭 (REQ-V19-011) */}
      {activeTab === 'templates' && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <TemplateManagement projectId={project.id} isAdmin={isProjectAdmin} />
        </div>
      )}

      {/* GitHub 연동 탭 (CHG-046: REQ-V46-011) */}
      {/* 이슈 타입 설정 탭 (CHG-052: REQ-V52-019) */}
      {activeTab === 'issue-types' && (
        <ProjectIssueTypeSettingsTab projectId={project.id} isProjectManager={isProjectManager} />
      )}

      {/* 워크플로우 탭 */}
      {activeTab === 'workflow' && (
        <div className="space-y-6">
          {/* 워크플로우 설정 (CHG-041 REQ-V41-015) */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">프로젝트 워크플로우</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  현재: {project.workflow.name}
                </p>
                <div className="flex items-center gap-1 flex-wrap mt-2">
                  {project.workflow.statuses.map((s, i) => (
                    <span key={s.statusId} className="flex items-center gap-1">
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium rounded-full"
                        style={{ backgroundColor: s.color, color: getContrastText(s.color) }}
                      >
                        {s.name}
                      </span>
                      {i < project.workflow.statuses.length - 1 && (
                        <span className="text-gray-300 text-[11px]">→</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
              {isProjectAdmin && (
                <button
                  onClick={() => setShowWorkflowModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                >
                  변경
                  <ArrowRight className="size-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* 워크플로우 변경 모달 */}
          {showWorkflowModal && (
            <WorkflowMigrationModal
              projectId={project.id}
              currentWorkflowId={project.workflow.workflowId}
              currentWorkflowName={project.workflow.name}
              onClose={() => setShowWorkflowModal(false)}
              onMigrated={() => {
                setShowWorkflowModal(false);
                queryClient.invalidateQueries({ queryKey: projectKeys.detail(project.id) });
              }}
            />
          )}

          {/* 작업 유형별 워크플로우 매핑 (CHG-042: REQ-V42-008) */}
          {isProjectAdmin && (
            <IssueTypeWorkflowMappingSection projectId={project.id} projectIssueTypes={projectIssueTypesData?.issueTypes} />
          )}
        </div>
      )}

      {activeTab === 'github' && (
        <GitHubSettingsTab project={project} isProjectManager={isProjectManager} />
      )}

      {/* 디자인 설정 탭 (Figma 연결 + 디자인 설정 통합) */}
      {activeTab === 'design' && (
        <div className="space-y-6">
          <FigmaSettingsTab project={project} isProjectManager={isProjectManager} />
          <DesignSettingsTab projectId={project.id} isProjectManager={isProjectManager} />
        </div>
      )}

      {/* 자동화 규칙 탭 (CHG-047: REQ-V47-013) */}
      {activeTab === 'automation' && (
        <AutomationRulesTab project={project} isProjectManager={isProjectManager} />
      )}

      {/* 알림 설정 탭 (CHG-048: REQ-V48-014) */}
      {activeTab === 'notifications' && (
        <NotificationSettingsTab projectId={project.id} isProjectManager={isProjectManager} />
      )}

      {/* 보안 탭 — 위험 구역(멤버 탭에서 이동, CHG-158 REQ-PS-004) + 보관 접근 기록(CHG-132 Phase E REQ-PA-023) */}
      {activeTab === 'access-history' && (
        <div className="space-y-6">
          {/* Danger Zone — PM만, 멤버 탭에서 이동 */}
          {isProjectManager && (
            <div className="bg-white border border-red-200 rounded-xl">
              <div className="p-5">
                <h3 className="text-sm font-semibold text-red-600">위험 구역</h3>
                <p className="text-sm text-gray-500 mt-1">아래 작업은 신중하게 진행해 주세요.</p>
              </div>
              {/* CHG-132: 프로젝트 보관 (삭제보다 먼저 권장) */}
              {!project.archivedAt && (
                <div className="px-5 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">프로젝트 보관</p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        진행이 끝난 프로젝트를 읽기 전용으로 전환합니다. 문서함은 졸업/유지/삭제 중에서 선택할 수 있습니다.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowArchiveModal(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-amber-300 text-amber-700 rounded-lg text-sm font-medium
                        hover:bg-amber-50 transition-colors shrink-0"
                    >
                      <Archive className="size-4" />
                      보관하기
                    </button>
                  </div>
                </div>
              )}
              <div className="px-5 pb-5 pt-1 border-t border-gray-100">
                <div className="pt-4">
                {!showDeleteConfirm ? (
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">프로젝트 삭제</p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        이 작업은 되돌릴 수 없습니다. 보관을 먼저 고려해 보세요.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium
                        hover:bg-red-50 transition-colors shrink-0"
                    >
                      <Trash2 className="size-4" />
                      프로젝트 삭제
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3 p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-700">
                      프로젝트를 삭제하려면 프로젝트 키 <strong>{project.projectKey}</strong>를 입력하세요.
                    </p>
                    <input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder={project.projectKey}
                      className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleDeleteProject}
                        disabled={deleteConfirmText !== project.projectKey || deleteProjectMutation.isPending}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium
                          hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        {deleteProjectMutation.isPending && <Spinner className="size-4 text-white" />}
                        삭제 확인
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmText('');
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        취소
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          )}

          {/* 보관 접근 기록 */}
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">보관 접근 기록</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                이 프로젝트가 보관된 후 비멤버가 요청한 읽기 권한 요청과 처리 이력입니다. 모든 프로젝트 멤버가 조회할 수 있습니다.
              </p>
            </div>
            <AccessRequestHistory scope={{ kind: 'project', projectId: project.id }} />
          </div>

          {/* CHG-132: 프로젝트 보관 모달 — 멤버 탭에서 이동 (트리거 버튼이 이 탭에 있으므로 모달도 함께 이동) */}
          <ArchiveProjectModal
            isOpen={showArchiveModal}
            onClose={() => setShowArchiveModal(false)}
            projectId={project.id}
            projectName={project.name}
            projectKey={project.projectKey}
            onArchived={() => {
              setShowArchiveModal(false);
              setCurrentProject(null);
              navigate('/projects');
            }}
          />
        </div>
      )}
      </div>
    </div>
  );
}
